import React, { useEffect, useState } from "react"
import { view } from "@risingstack/react-easy-state"
import { useHistory, Redirect } from "react-router-dom"
import styled from "styled-components"
import {
  EyeOutlined,
  InboxOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  EditOutlined,
  DownOutlined,
} from "@ant-design/icons"
import {
  message,
  Button,
  Empty,
  Pagination,
  Table,
  List,
  Input,
  Tooltip,
  Modal,
  Upload,
  Popconfirm,
  Dropdown,
  Menu,
} from "antd"

import { hideExportButtons } from "utils/export"
import StyledBox from "components/common/styled-components/StyledBox"
import StyledList from "components/common/styled-components/StyledList"
import { staleWhileRevalidate, networkOnly } from "utils/render-strategies"
import globalStore from "store/index"
import SalaryRangesService from "services/users/salary-ranges.service"

const StyledPageContent = styled.div`
  & > .action-buttons.top {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }
`

const SalaryRangesIndexPage = (props) => {
  const history = useHistory()
  const pageState = globalStore.ui.configuration.salaryRanges.index
  const [exporting, setExporting] = useState(false)
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [fileList, setFileList] = useState([])
  const [importing, setImporting] = useState(false)
  const [exportingAuditReports, setExportingAuditReports] = useState(false)

  const accessibleFeatures = globalStore.currentUser?.accessible_features || []

  useEffect(() => {
    const fetchSalaryRanges = async () => {
      const pageState = globalStore.ui.configuration.salaryRanges.index

      await SalaryRangesService.index({ pageState })

      const { serverStatus, salaryRangesErrors } = pageState

      if (serverStatus.status !== 200) {
        message.error("Oops!! something went wrong")
        return
      }

      if (salaryRangesErrors) {
        message.error(salaryRangesErrors[0] || "Coundn't fetch salary ranges")
        return
      }
    }

    fetchSalaryRanges()
  }, [])

  const repaginate = async (page, pageSize, jobFamily) => {
    if (jobFamily) {
      await SalaryRangesService.index({ pageState, page, pageSize, jobFamily })
      return
    }

    await SalaryRangesService.index({ pageState, page, pageSize })
  }

  const handleCreate = () => {
    history.push("/configuration/salary-ranges/new")
  }

  const openImportModal = () => {
    setImportModalVisible(true)
  }

  const handleImport = async () => {
    if (fileList.length === 0) return

    const tempState = {}

    const formData = new FormData()

    formData.append("file", fileList[0])

    setImporting(true)
    await SalaryRangesService.import({ pageState: tempState, formData })
    setImporting(false)

    setImportModalVisible(false)

    message.success(
      "Import started. Kindly check import page in Report Center to see imported salary ranges"
    )
  }

  const handleExport = async () => {
    const tempState = {}

    setExporting(true)
    await SalaryRangesService.export({ pageState: tempState })
    setExporting(false)

    message.success(
      "Export started. Kindly check export page in Report Center to see exported salary ranges"
    )
  }

  const handleExportAuditReports = async () => {
    const tempState = {}

    setExportingAuditReports(true)
    await SalaryRangesService.auditReports({ pageState: tempState })
    setExportingAuditReports(false)

    message.success(
      "Export started. Kindly check export page in Report Center to see exported audit reports"
    )
  }

  const handleExportAuditReport = async (salaryRangeId) => {
    const tempState = {}

    await SalaryRangesService.auditReport({
      pageState: tempState,
      salaryRangeId,
    })

    message.success(
      "Export started. Kindly check export page in Report Center to see exported audit reports"
    )
  }

  const handleCardItemClick = (salaryRangeId) => {
    history.push(`/configuration/salary-ranges/${salaryRangeId}`)
  }

  const handleEdit = (salaryRangeId) => {
    history.push(`/configuration/salary-ranges/${salaryRangeId}/edit`)
  }

  const handleDelete = async (salaryRangeId) => {
    const tempState = {}

    await SalaryRangesService.delete({ pageState: tempState, salaryRangeId })

    const { serverStatus } = tempState

    if (serverStatus.status !== 200) return

    globalStore.ui.configuration.salaryRanges.index = {
      ...globalStore.ui.configuration.salaryRanges.index,
      salary_ranges: salary_ranges.filter(
        (salary_range) => salary_range.id !== salaryRangeId
      ),
    }
  }

  const exportButtonMenu = (
    <Menu>
      <Menu.Item key="0">
        <Button type="link" ghost loading={exporting} onClick={handleExport}>
          Export
        </Button>
      </Menu.Item>
      <Menu.Item key="1">
        <Button
          type="link"
          ghost
          loading={exportingAuditReports}
          onClick={handleExportAuditReports}
        >
          {exportingAuditReports
            ? "Exporting Audit Reports..."
            : "Export Audit Reports"}
        </Button>
      </Menu.Item>
    </Menu>
  )

  const {
    salary_ranges,
    salary_rangesMeta,
    API_USERS_SALARY_RANGES_INDEX_STATUS,
    displayType = "table",
  } = pageState

  const {
    total,
    page: currentPage,
    page_size: pageSize,
    criteria_keys,
  } = salary_rangesMeta || {}

  const renderList = () => {
    return (
      <StyledList>
        {salary_ranges.map((sr) => (
          <div
            className="item-card"
            key={`item-card-${sr.id}`}
            onClick={() => handleCardItemClick(sr.id)}
          >
            <div className="details-box">
              <div className="name-box">
                <div className="name">{sr.name}</div>
                <div>{}</div>
              </div>
            </div>
            <div className="icon-box">
              <Button type="link">
                <EyeOutlined />
              </Button>
            </div>
          </div>
        ))}
      </StyledList>
    )
  }

  const renderContents = () => {
    if (salary_ranges.length === 0) {
      return (
        <Empty>
          <Button type="primary" ghost onClick={handleCreate}>
            Create Salary Range
          </Button>
        </Empty>
      )
    }

    const pagination = (
      <Pagination
        current={currentPage}
        total={total}
        onChange={repaginate}
        onShowSizeChange={repaginate}
        pageSize={pageSize || 25}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    )

    const tableColumns = [
      ...criteria_keys.map((ck) => ({
        title: ck,
        dataIndex: ck,
        key: ck,
        render: (filteredCriteria, row) => {

          if (!filteredCriteria) return "--" // if null
          if (!filteredCriteria[0]) return "--" // If [null]
          if (filteredCriteria.length === 0) return "---" // if []

          const dataSource = filteredCriteria.filter((fc) => fc) // FIXME: need to fix

          return (
            <List
              dataSource={dataSource}
              renderItem={(values) => {
                return values.join(", ")
              }}
            ></List>
          )
        },
      })),
      ...[
        // {
        //   title: "Criteria",
        //   dataIndex: "criteria",
        //   key: "criteria",
        //   render: (criteria, row) => {
        //     return (
        //       <List
        //         dataSource={criteria}
        //         renderItem={(conditions) =>
        //           conditions.map((condition, index) => (
        //             <Tag color="purple" key={index}>
        //               {condition.field}: {condition.values.join(", ")}
        //             </Tag>
        //           ))
        //         }
        //       ></List>
        //     )
        //   },
        // },
        {
          title: "Currency",
          dataIndex: "currency",
          key: "currency",
        },
        {
          title: "Minimum",
          dataIndex: "minimum",
          key: "minimum",
        },
        {
          title: "Median",
          dataIndex: "median",
          key: "median",
        },
        {
          title: "Maximum",
          dataIndex: "maximum",
          key: "maximum",
        },
        {
          title: "Action",
          dataIndex: "action",
          key: "action",
          align: "center",
          render: (value, sr) => {
            return (
              <div>
                <Tooltip title="View">
                  <Button
                    type="link"
                    onClick={() => handleCardItemClick(sr.id)}
                  >
                    <EyeOutlined />
                  </Button>
                </Tooltip>
                <Tooltip title="Edit">
                  <Button type="link" onClick={() => handleEdit(sr.id)}>
                    <EditOutlined />
                  </Button>
                </Tooltip>
                <Tooltip title="Delete">
                  <Popconfirm
                    title="Are you sure to delete?"
                    onConfirm={() => handleDelete(sr.id)}
                    onCancel={() => {}}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link">
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                </Tooltip>
                <Tooltip title="Export audit report">
                  <Button
                    type="link"
                    onClick={() => handleExportAuditReport(sr.id)}
                  >
                    <FileSearchOutlined />
                  </Button>
                </Tooltip>
              </div>
            )
          },
        },
      ],
    ]

    const dataSource = salary_ranges.map((sr) => {
      const retval = { ...sr, key: sr.id }
      criteria_keys.forEach((ck) => {
        retval[ck] = sr.criteria.map((conditions) => {
          const relevantCondition = conditions.find((c) => c.field === ck)
          if (!relevantCondition) return null
          return relevantCondition.values
        })
      })
      return retval
    })

    const renderTable = () => {
      return (
        <Table
          columns={tableColumns}
          dataSource={dataSource}
          pagination={false}
          scroll={{
            x: "max-content",
          }}
        ></Table>
      )
    }

    const renderInfo = () => {
      if (displayType === "table") return renderTable()
      return renderList()
    }

    return (
      <div>
        {pagination}

        {networkOnly({
          status: API_USERS_SALARY_RANGES_INDEX_STATUS,
          data: salary_ranges,
          render: renderInfo,
        })}

        {pagination}
      </div>
    )
  }

  const renderPageContent = () => {
    const jobFamilySearch = (
      <Input.Search
        placeholder="Job Family"
        onSearch={(jobFamily) => repaginate(1, pageSize, jobFamily)}
        style={{ width: 200, marginBottom: "10px" }}
      />
    )

    return (
      <StyledPageContent>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>{jobFamilySearch}</div>
          <div className="action-buttons top">
            <Tooltip title="Create or update multiple records by upload">
              <Button onClick={openImportModal} style={{ marginRight: 10 }}>
                Import
              </Button>
            </Tooltip>
            {hideExportButtons(accessibleFeatures) && (
              <React.Fragment>
                <Dropdown overlay={exportButtonMenu}>
                  <Button style={{ marginRight: 10 }}>
                    Export <DownOutlined />
                  </Button>
                </Dropdown>

                {/* <Tooltip title="Prepares report for download, available under export section">
                  <Button
                    loading={exporting}
                    onClick={handleExport}
                    style={{ marginRight: 10 }}
                  >
                    Export
                  </Button>
                </Tooltip>
                <Tooltip title="Export audit reports of salary ranges">
                  <Button
                    loading={exportingAuditReports}
                    onClick={handleExportAuditReports}
                    style={{ marginRight: 10 }}
                  >
                    {exportingAuditReports
                      ? "Exporting Audit Reports..."
                      : "Export Audit Reports"}
                  </Button>
                </Tooltip> */}
              </React.Fragment>
            )}
            <Tooltip title="Create a new record">
              <Button type="primary" ghost onClick={handleCreate}>
                Create Salary Range
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="content">{renderContents()}</div>
      </StyledPageContent>
    )
  }
  const handleSampleFileDownload = () => {
    window.open(
      "https://storage.googleapis.com/ps-in-images-compensation/images/pdf/1094/salary_range_importer_sample.xlsx?1630654470"
    )
  }

  const uploadProps = {
    accept:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
    onRemove: (file) => {
      const index = fileList.indexOf(file)
      const newFileList = fileList.slice()
      newFileList.splice(index, 1)

      setFileList(newFileList)
    },
    beforeUpload: (file) => {
      setFileList([file])
      return false
    },
    fileList,
  }

  if (
    accessibleFeatures?.length > 0 &&
    !accessibleFeatures.includes("fitment-data-view")
  )
    return <Redirect to="/" />

  return (
    <div className="page-content salary-ranges-page">
      <StyledBox>
        {staleWhileRevalidate({
          status: API_USERS_SALARY_RANGES_INDEX_STATUS,
          data: salary_ranges,
          render: renderPageContent,
        })}
      </StyledBox>
      <Modal
        title="Import"
        visible={importModalVisible}
        confirmLoading={importing}
        closable={false}
        maskClosable={false}
        destroyOnClose
        onOk={handleImport}
        okText="Import"
        onCancel={() => setImportModalVisible(false)}
      >
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Please ensure data accuracy in your file for upload. After upload,
            each associated records will be updated directly.
          </p>
        </Upload.Dragger>
        <Button type="link" onClick={handleSampleFileDownload}>
          Sample Import File
        </Button>
      </Modal>
    </div>
  )
}

export default view(SalaryRangesIndexPage)
